import React from 'react'
import jwt from 'jsonwebtoken'
import Session from '../../Main/Services/User/session'
import { KEY_LOCAL_STORAGE_TOKEN } from '../Auth.constants'
import { GetWrapperAuth0, useAuth0Adapter } from './auth0/Auth0Adapter'
import { GetWrapperStytch, useStytchAdapter } from './stytch/StytchAdapter'

export interface WrapperProps {
  children: React.ReactNode
}

export enum AuthProviders {
  AUTH0 = 'AUTH0',
  STYTCH = 'STYTCH'
}

export interface logoutProps {
  federated?: boolean
}

export interface IAuthAdapter {
  init: () => Promise<void>
  isAuthenticated: () => Promise<boolean>
  generateToken: () => Promise<string>
  logout: (props?: logoutProps) => void
  getRenderLogin: (email?: string) => JSX.Element
  redirectLogin: (email?: string) => Promise<void>
}

interface JwtPayload {
  iss?: string
}

const authProviderMapper: Record<AuthProviders, () => IAuthAdapter> = {
  AUTH0: () => useAuth0Adapter(),
  STYTCH: () => useStytchAdapter()
}

const providerKeyMapper: Record<string, AuthProviders> = {
  auth0: AuthProviders.AUTH0,
  stytch: AuthProviders.STYTCH
}

type WrapperComponent = React.FC<{ children: React.ReactNode }>

const wrapperAuthMapper: Record<AuthProviders, WrapperComponent> = {
  AUTH0: ({ children }) => GetWrapperAuth0({ children }),
  STYTCH: ({ children }) => GetWrapperStytch({ children })
}

export const getAuthAdapter = (provider: AuthProviders | undefined): IAuthAdapter | undefined => {
  if (!provider) {
    return undefined
  }
  const providerAdapter = authProviderMapper[provider]

  if (!providerAdapter) {
    throw new Error(`Authentication provider not implemented: ${provider}`)
  }

  return providerAdapter()
}

const getProviderInQueryParams = (): string | undefined => {
  const search = window.location.search
  return new URLSearchParams(search).get('provider') ?? undefined
}

const getProviderFromToken = (): AuthProviders | undefined => {
  const token = Session.get(KEY_LOCAL_STORAGE_TOKEN)
  if (!token) return undefined
  const payload = jwt.decode(token) as JwtPayload | null
  const iss = payload?.iss
  if (iss?.includes(AuthProviders.AUTH0.toLowerCase())) return AuthProviders.AUTH0
  if (iss?.includes(AuthProviders.STYTCH.toLowerCase())) return AuthProviders.STYTCH
}

export const getAuthProvider = (): AuthProviders | undefined => {
  const key = getProviderInQueryParams()
  return providerKeyMapper[key?.toLowerCase() ?? ''] ?? getProviderFromToken() ?? undefined
}

export const WrapperAuthProvider: WrapperComponent = ({ children }) => {
  const authProvider = getAuthProvider()
  if (!authProvider) {
    return <>{children}</>
  }
  const SelectedWrapper = wrapperAuthMapper[authProvider]
  return <SelectedWrapper>{children}</SelectedWrapper>
}

export const getEmailInQueryParams = (): string | undefined => {
  const search = window.location.search
  return new URLSearchParams(search).get('email') ?? ''
}
